import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building, Users, FileText, DollarSign, Clock, Award, 
  ChevronRight, ChevronLeft, ArrowRight, Lock, CheckCircle, BarChart3,
  Sparkles, Info, Play, BookOpen, Target, Upload, Eye, X, Wifi
} from 'lucide-react';
import useWebSocket from '../../hooks/useWebSocket';
import NotificationToast from '../RealTimeNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Assessment Flow Phases
const ASSESSMENT_PHASES = [
  { id: 1, name: 'Qualitative', shortName: 'Qualitative', icon: FileText, description: 'Self-Assessment', color: 'indigo' },
  { id: 2, name: 'Payment', shortName: 'Payment', icon: DollarSign, description: 'Certification Fee', color: 'emerald' },
  { id: 3, name: 'Quantitative', shortName: 'Quantitative', icon: BarChart3, description: 'Data & Metrics', color: 'purple' },
  { id: 4, name: 'Evaluation', shortName: 'Evaluation', icon: Clock, description: 'Expert Review (~2 weeks)', color: 'orange' },
  { id: 5, name: 'Results', shortName: 'Results', icon: Award, description: 'Your Certification', color: 'yellow' },
];

// Certification Tiers
const CERTIFICATION_TIERS = [
  { name: 'Luminary', range: '85-100', color: 'from-amber-400 to-yellow-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50', icon: '🌟' },
  { name: 'Inspired', range: '65-84', color: 'from-purple-400 to-indigo-500', textColor: 'text-purple-700', bgColor: 'bg-purple-50', icon: '✨' },
  { name: 'Aspiring', range: '40-64', color: 'from-emerald-400 to-teal-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', icon: '🌱' },
];

const AssessmentEngine = ({ setActiveModule, initialTab = null }) => {
  const navigate = useNavigate();
  // Tab state will be updated once we know if user has active assessment
  const [activeTab, setActiveTab] = useState(initialTab || 'information');
  const [loading, setLoading] = useState(true);
  const [organizationData, setOrganizationData] = useState(null);
  const [assessmentConfig, setAssessmentConfig] = useState(null);
  const [assessmentPhases, setAssessmentPhases] = useState([]);
  const [pillarNames, setPillarNames] = useState([]);
  const [showAssessmentView, setShowAssessmentView] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [detailedStatus, setDetailedStatus] = useState(null);
  const [showViewMode, setShowViewMode] = useState(false);
  const [viewModePhase, setViewModePhase] = useState('qualitative');
  const [analytics, setAnalytics] = useState(null);
  const [hasSetInitialTab, setHasSetInitialTab] = useState(false);
  
  // Real-time notification state
  const [notifications, setNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(null);
  const [evaluatorStatus, setEvaluatorStatus] = useState({ is_online: false, evaluator_name: null });
  
  // Get organization ID from storage - PRIORITIZE sessionStorage (tab-specific)
  const getOrganizationId = () => {
    // First check sessionStorage (tab-specific, set by PlatformApp login)
    const sessionOrgId = sessionStorage.getItem('cness_organization_id');
    if (sessionOrgId && sessionOrgId !== 'null' && sessionOrgId !== '') {
      console.log('[AssessmentEngine] Using sessionStorage org_id:', sessionOrgId);
      return sessionOrgId;
    }
    
    // Fallback to localStorage
    const localOrgId = localStorage.getItem('organization_id');
    if (localOrgId && localOrgId !== 'null' && localOrgId !== '') {
      console.log('[AssessmentEngine] Using localStorage org_id:', localOrgId);
      return localOrgId;
    }
    
    // Try userData in localStorage
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      if (userData.organization_id) {
        console.log('[AssessmentEngine] Using userData org_id:', userData.organization_id);
        return userData.organization_id;
      }
    } catch (e) {
      console.error('Error parsing userData:', e);
    }
    
    console.warn('[AssessmentEngine] No organization_id found!');
    return null;
  };
  
  const organizationId = getOrganizationId();
  
  // Handle incoming WebSocket messages
  const handleWebSocketMessage = useCallback((message) => {
    console.log('[WebSocket] Received:', message);
    
    // Update notification state
    const notification = {
      id: Date.now(),
      ...message
    };
    
    setShowNotification(notification);
    
    // If evaluation progress update, also update local state
    if (message.type === 'evaluation_progress' && detailedStatus) {
      setDetailedStatus(prev => ({
        ...prev,
        phases: {
          ...prev?.phases,
          evaluation: {
            ...prev?.phases?.evaluation,
            progress: message.progress
          }
        }
      }));
    }
    
    // If evaluation complete, refresh detailed status
    if (message.type === 'evaluation_complete') {
      // Refresh status from server
      fetchDetailedStatus();
    }
  }, [detailedStatus]);
  
  // Fetch detailed status helper
  const fetchDetailedStatus = async () => {
    if (!organizationId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment/detailed-status/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        setDetailedStatus(data);
      }
    } catch (e) {
      console.error('Error fetching detailed status:', e);
    }
  };
  
  // WebSocket connection for real-time updates
  const { isConnected: wsConnected } = useWebSocket(organizationId, handleWebSocketMessage);

  // Fetch evaluator online status
  const fetchEvaluatorStatus = async () => {
    if (!organizationId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/evaluator/online-status/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[AssessmentEngine] Evaluator status:', data);
        setEvaluatorStatus(data);
      }
    } catch (e) {
      console.log('Error fetching evaluator status');
    }
  };

  useEffect(() => {
    fetchData();
    fetchPillarNames();
    // Fetch evaluator status immediately and poll every 15 seconds
    fetchEvaluatorStatus();
    const statusInterval = setInterval(fetchEvaluatorStatus, 15000);
    return () => clearInterval(statusInterval);
  }, []);

  const fetchData = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      // Fetch organization details
      const orgResponse = await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}`);
      const orgData = await orgResponse.json();
      setOrganizationData(orgData);

      // Fetch assessment config based on company size
      const size = orgData?.organization_size?.toUpperCase() || 'MEDIUM';
      const configResponse = await fetch(`${BACKEND_URL}/api/assessment/config/${size}`);
      const configData = await configResponse.json();
      setAssessmentConfig(configData);

      // Fetch detailed assessment status
      try {
        const statusResponse = await fetch(`${BACKEND_URL}/api/assessment/detailed-status/${organizationId}`);
        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          setDetailedStatus(statusData);
          if (statusData.session_id) {
            setCurrentSessionId(statusData.session_id);
            // Fetch analytics if we have completed at least qualitative
            if (statusData.phases?.qualitative?.status === 'completed') {
              fetchAnalytics(statusData.session_id);
            }
          }
          // Auto-switch to Assessment Dashboard tab if user has started an assessment
          // (only on first load, not when returning from embedded view)
          if (!hasSetInitialTab && statusData.has_assessment) {
            setActiveTab('assessment');
            setHasSetInitialTab(true);
          }
        }
      } catch (e) {
        console.log('No detailed status available');
      }

      // Fetch assessment phases/progress for this organization
      try {
        const progressResponse = await fetch(`${BACKEND_URL}/api/admin/assessment-progress/${organizationId}`);
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          setAssessmentPhases(progressData);
        }
      } catch (e) {
        console.log('No existing assessment progress');
      }

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment/analytics/${sessionId}/${organizationId}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (e) {
      console.log('Error fetching analytics:', e);
    }
  };

  const fetchPillarNames = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/enhanced-assessment/questions`);
      const data = await response.json();
      
      // Extract unique pillar names from questions
      const pillarsMap = {};
      (data.questions || []).forEach(q => {
        if (q.pillar_number && q.pillar_name && !pillarsMap[q.pillar_number]) {
          pillarsMap[q.pillar_number] = q.pillar_name;
        }
      });
      
      // Convert to array sorted by pillar number
      const pillarsArray = Object.entries(pillarsMap)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([num, name]) => ({ number: Number(num), name }));
      
      setPillarNames(pillarsArray);
    } catch (error) {
      console.error('Error fetching pillar names:', error);
    }
  };

  const startAssessment = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/assessment/generate-questions/${organizationId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSessionId(data.session_id);
        setQuestions([
          ...(data.qualitative_questions || []),
          ...(data.quantitative_questions || [])
        ]);
        setShowAssessmentView(true);
        setShowViewMode(false);
      }
    } catch (error) {
      console.error('Error starting assessment:', error);
    }
  };

  const resumeAssessment = async () => {
    // Use existing session from detailedStatus
    if (detailedStatus?.session_id) {
      const response = await fetch(`${BACKEND_URL}/api/assessment/generate-questions/${organizationId}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentSessionId(data.session_id);
        setQuestions([
          ...(data.qualitative_questions || []),
          ...(data.quantitative_questions || [])
        ]);
        setShowAssessmentView(true);
        setShowViewMode(false);
      }
    }
  };

  const viewCompletedPhase = async (phase) => {
    if (!currentSessionId && !detailedStatus?.session_id) return;
    setViewModePhase(phase);
    setShowViewMode(true);
    setShowAssessmentView(false);
  };

  const goToPillarsLearning = () => {
    if (setActiveModule) {
      setActiveModule('pillars');
    }
  };

  // Get action button text and action based on detailed status - DATABASE DRIVEN
  const getActionButton = () => {
    if (!detailedStatus?.has_assessment) {
      return { text: 'Start Assessment', action: startAssessment, icon: Play, color: 'bg-white text-indigo-600', disabled: false };
    }
    
    // State-aware button logic
    switch (detailedStatus.status) {
      case 'qualitative_not_started':
        return { text: 'Begin Qualitative', action: resumeAssessment, icon: Play, color: 'bg-indigo-600 text-white', disabled: false };
      case 'qualitative_in_progress':
        return { text: 'Resume Qualitative', action: resumeAssessment, icon: Play, color: 'bg-indigo-600 text-white', disabled: false };
      case 'qualitative_complete':
        return { text: 'Complete Payment', action: resumeAssessment, icon: DollarSign, color: 'bg-amber-500 text-white', disabled: false };
      case 'payment_pending':
        return { text: 'Complete Payment', action: resumeAssessment, icon: DollarSign, color: 'bg-amber-500 text-white', disabled: false };
      case 'payment_complete':
        return { text: 'Start Quantitative', action: resumeAssessment, icon: BarChart3, color: 'bg-purple-600 text-white', disabled: false };
      case 'quantitative_not_started':
        return { text: 'Start Quantitative', action: resumeAssessment, icon: BarChart3, color: 'bg-purple-600 text-white', disabled: false };
      case 'quantitative_in_progress':
        return { text: 'Resume Quantitative', action: resumeAssessment, icon: BarChart3, color: 'bg-purple-600 text-white', disabled: false };
      case 'quantitative_complete':
        return { text: 'Awaiting Evaluation', action: () => setActiveTab('analytics'), icon: Clock, color: 'bg-orange-500 text-white', disabled: true };
      case 'pending_evaluation':
        return { text: 'Awaiting Evaluation', action: () => setActiveTab('analytics'), icon: Clock, color: 'bg-orange-500 text-white', disabled: true };
      case 'evaluation_complete':
        return { text: 'View Results', action: () => setActiveTab('analytics'), icon: Award, color: 'bg-emerald-600 text-white', disabled: false };
      default:
        return { text: 'Continue Assessment', action: resumeAssessment, icon: Play, color: 'bg-indigo-600 text-white', disabled: false };
    }
  };

  // Get status badge info - ALL STATES, DATABASE DRIVEN
  const getStatusBadge = () => {
    if (!detailedStatus?.has_assessment) {
      return { text: 'Not Started', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: null };
    }
    
    switch (detailedStatus.status) {
      case 'qualitative_not_started':
        return { 
          text: 'Ready to Begin', 
          color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
          icon: Play
        };
      case 'qualitative_in_progress':
        return { 
          text: `Qualitative In Progress (${detailedStatus.phases?.qualitative?.completed}/${detailedStatus.phases?.qualitative?.total})`, 
          color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
          icon: Clock
        };
      case 'qualitative_complete':
        return { 
          text: 'Qualitative Complete — Payment Required', 
          color: 'bg-amber-100 text-amber-700 border-amber-200',
          icon: DollarSign
        };
      case 'payment_pending':
        return { 
          text: 'Payment Required', 
          color: 'bg-amber-100 text-amber-700 border-amber-200',
          icon: DollarSign
        };
      case 'payment_complete':
        return { 
          text: 'Payment Complete — Start Quantitative', 
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          icon: CheckCircle
        };
      case 'quantitative_not_started':
        return { 
          text: 'Payment Complete — Quantitative Pending', 
          color: 'bg-purple-100 text-purple-700 border-purple-200',
          icon: BarChart3
        };
      case 'quantitative_in_progress':
        return { 
          text: `Quantitative In Progress (${detailedStatus.phases?.quantitative?.completed}/${detailedStatus.phases?.quantitative?.total})`, 
          color: 'bg-purple-100 text-purple-700 border-purple-200',
          icon: Clock
        };
      case 'quantitative_complete':
        return { 
          text: 'Assessment Complete — Awaiting Evaluation', 
          color: 'bg-orange-100 text-orange-700 border-orange-200',
          icon: Clock
        };
      case 'pending_evaluation':
        return { 
          text: `Evaluation In Progress (${detailedStatus.phases?.evaluation?.progress || 0}%)`, 
          color: 'bg-orange-100 text-orange-700 border-orange-200',
          icon: Clock
        };
      case 'evaluation_complete':
        return { 
          text: 'Evaluation Complete — View Results', 
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          icon: Award
        };
      default:
        return { 
          text: detailedStatus.status_message || 'In Progress', 
          color: 'bg-blue-100 text-blue-700 border-blue-200',
          icon: Clock
        };
    }
  };

  // Determine if user has certification
  const hasCertification = organizationData?.certification_tier && 
    ['Aspiring', 'Inspired', 'Luminary'].includes(organizationData.certification_tier);

  // Get display values
  const companySize = assessmentConfig?.company_size || organizationData?.organization_size || 'N/A';
  const companySizeDisplay = assessmentConfig?.config?.display_name || companySize;
  const employeeRange = assessmentConfig?.config?.employee_range || 'N/A';
  const totalQuestions = assessmentConfig?.config?.total_questions || assessmentConfig?.question_distribution?.total || 0;
  const qualitativeQuestions = assessmentConfig?.config?.qualitative_questions || assessmentConfig?.question_distribution?.qualitative || 0;
  const quantitativeQuestions = assessmentConfig?.config?.quantitative_questions || assessmentConfig?.question_distribution?.quantitative || 0;
  const fee = assessmentConfig?.fee || assessmentConfig?.config?.assessment_fee || 0;

  // Get action button and status badge
  const actionButton = getActionButton();
  const statusBadge = getStatusBadge();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading assessment center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Real-Time Notification Toast */}
      {showNotification && (
        <NotificationToast
          notification={showNotification}
          onClose={() => setShowNotification(null)}
          autoClose={showNotification.type === 'evaluation_complete' ? 10000 : 5000}
        />
      )}
      
      {/* View Responses Modal */}
      {showViewMode && (
        <ViewResponsesModal
          sessionId={currentSessionId || detailedStatus?.session_id}
          organizationId={organizationId}
          phase={viewModePhase}
          onClose={() => setShowViewMode(false)}
        />
      )}

      {/* Show either Assessment View OR the normal dashboard */}
      {showAssessmentView ? (
        <EmbeddedAssessmentView 
          questions={questions}
          sessionId={currentSessionId}
          organizationId={organizationId}
          organizationName={organizationData?.name}
          companySize={companySize}
          assessmentConfig={assessmentConfig}
          onBack={() => {
            setShowAssessmentView(false);
            fetchData(); // Refresh data after coming back
          }}
          onPhaseComplete={(phase) => {
            fetchData(); // Refresh when phase completes
          }}
        />
      ) : (
        <>
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Assessment Center</h1>
                <p className="text-slate-600 mt-1">
                  {organizationData?.name || 'Your Organization'} • {organizationData?.organization_size || companySizeDisplay || 'Enterprise'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Only show when evaluator is ONLINE - hide when offline */}
                {evaluatorStatus?.is_online && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 rounded-xl animate-pulse">
                    <Wifi className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      Evaluator Online{evaluatorStatus.evaluator_name && ` - ${evaluatorStatus.evaluator_name}`}
                    </span>
                  </div>
                )}
                {hasCertification && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300 rounded-xl">
                    <Award className="w-5 h-5 text-amber-600" />
                    <span className="font-bold text-amber-800">{organizationData.certification_tier}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('information')}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-all border-b-2 ${
              activeTab === 'information'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Info className="w-4 h-4" />
            Information Board
          </button>
          <button
            onClick={() => setActiveTab('assessment')}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-all border-b-2 ${
              activeTab === 'assessment'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Play className="w-4 h-4" />
            Assessment Dashboard
          </button>
          <button
            onClick={() => {
              // Accessible after qualitative completion OR if has certification
              const canAccess = detailedStatus?.phases?.qualitative?.status === 'completed' || hasCertification;
              if (canAccess) setActiveTab('analytics');
            }}
            disabled={!detailedStatus?.phases?.qualitative?.status && detailedStatus?.phases?.qualitative?.status !== 'completed' && !hasCertification}
            className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-all border-b-2 ${
              activeTab === 'analytics'
                ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                : (detailedStatus?.phases?.qualitative?.status === 'completed' || hasCertification)
                ? 'border-transparent text-slate-600 hover:text-slate-900'
                : 'border-transparent text-slate-400 cursor-not-allowed'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Analytics & Reports
            {!detailedStatus?.phases?.qualitative?.status && detailedStatus?.phases?.qualitative?.status !== 'completed' && !hasCertification && (
              <Lock className="w-3 h-3 ml-1" />
            )}
          </button>
        </div>
      </div>

      {/* Information Board Tab */}
      {activeTab === 'information' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Company Size Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Building className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Your Company Size</p>
                  <p className="text-xl font-bold text-slate-900">{companySizeDisplay}</p>
                  <p className="text-xs text-slate-500">{employeeRange} employees</p>
                </div>
              </div>
            </div>

            {/* Total Questions Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Total Questions</p>
                  <p className="text-xl font-bold text-slate-900">{totalQuestions}</p>
                  <p className="text-xs text-slate-500">Qualitative: {qualitativeQuestions} | Quantitative: {quantitativeQuestions}</p>
                </div>
              </div>
            </div>

            {/* Assessment Fee Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Assessment Fee</p>
                  <p className="text-xl font-bold text-slate-900">${fee.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">One-time certification fee</p>
                </div>
              </div>
            </div>
          </div>

          {/* Assessment Roadmap */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Assessment Roadmap</h3>
            <div className="flex items-center justify-between">
              {ASSESSMENT_PHASES.map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <React.Fragment key={phase.id}>
                    <div className="flex-1 text-center">
                      <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3 ${
                        phase.color === 'indigo' ? 'bg-indigo-100' :
                        phase.color === 'emerald' ? 'bg-emerald-100' :
                        phase.color === 'purple' ? 'bg-purple-100' :
                        phase.color === 'orange' ? 'bg-orange-100' :
                        'bg-amber-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          phase.color === 'indigo' ? 'text-indigo-600' :
                          phase.color === 'emerald' ? 'text-emerald-600' :
                          phase.color === 'purple' ? 'text-purple-600' :
                          phase.color === 'orange' ? 'text-orange-600' :
                          'text-amber-600'
                        }`} />
                      </div>
                      <p className="text-sm font-bold text-slate-900">{phase.name}</p>
                      <p className="text-xs text-slate-500">{phase.description}</p>
                      {phase.id === 1 && <p className="text-xs font-semibold text-indigo-600 mt-1">{qualitativeQuestions} Questions</p>}
                      {phase.id === 2 && <p className="text-xs font-semibold text-emerald-600 mt-1">${fee.toLocaleString()}</p>}
                      {phase.id === 3 && <p className="text-xs font-semibold text-purple-600 mt-1">{quantitativeQuestions} Questions</p>}
                      {phase.id === 4 && <p className="text-xs font-semibold text-orange-600 mt-1">~2 Weeks</p>}
                      {phase.id === 5 && <p className="text-xs font-semibold text-amber-600 mt-1">Your Score</p>}
                    </div>
                    {idx < ASSESSMENT_PHASES.length - 1 && (
                      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Assessment Timeline Info */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold mb-2">Assessment Timeline</h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  The complete CNESS certification process takes approximately <span className="font-bold text-white">3-4 weeks</span>. 
                  After completing your self-assessment and submitting payment, our expert panel will evaluate your responses 
                  and supporting evidence within <span className="font-bold text-white">2 weeks</span>. You&apos;ll receive detailed feedback 
                  and your certification tier upon completion.
                </p>
              </div>
            </div>
          </div>

          {/* Certification Tiers */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Certification Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {CERTIFICATION_TIERS.map((tier) => (
                <div key={tier.name} className={`${tier.bgColor} rounded-xl p-5 border border-slate-200`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{tier.icon}</span>
                    <div>
                      <p className={`font-bold ${tier.textColor}`}>{tier.name}</p>
                      <p className="text-xs text-slate-600">Score: {tier.range}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-1">Ready to Begin?</h3>
                <p className="text-indigo-100">Start your certification journey today</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={goToPillarsLearning}
                  className="px-5 py-3 bg-white/10 rounded-xl font-semibold hover:bg-white/20 transition-all flex items-center gap-2"
                >
                  <BookOpen className="w-5 h-5" />
                  Learn 6 Pillars First
                </button>
                {/* Show correct button based on assessment status */}
                {!detailedStatus?.has_assessment ? (
                  <button
                    onClick={startAssessment}
                    className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-lg"
                  >
                    Start Assessment
                    <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setActiveTab('assessment')}
                    className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-bold hover:bg-indigo-50 transition-all flex items-center gap-2 shadow-lg"
                  >
                    Go to Assessment Dashboard
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Dashboard Tab */}
      {activeTab === 'assessment' && (
        <div className="space-y-6">
          {/* Status Card - Dynamic based on assessment state */}
          <div className={`rounded-2xl shadow-xl overflow-hidden ${
            detailedStatus?.status === 'payment_pending' ? 'bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600' :
            detailedStatus?.status === 'pending_evaluation' ? 'bg-gradient-to-r from-orange-500 via-orange-600 to-red-600' :
            detailedStatus?.status === 'evaluation_complete' ? 'bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-600' :
            'bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700'
          }`}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">CNESS Certification Assessment</h2>
                    <p className="text-white/70">{organizationData?.name || 'Your Organization'}</p>
                  </div>
                </div>
                
                {/* Status Badge and Action Button */}
                <div className="flex items-center gap-4">
                  <div className={`px-4 py-2 rounded-xl border backdrop-blur-sm ${statusBadge.color}`}>
                    <span className="font-semibold text-sm">{statusBadge.text}</span>
                  </div>
                  
                  {!hasCertification && (
                    <button
                      onClick={actionButton.action}
                      disabled={actionButton.disabled}
                      className={`px-6 py-3 rounded-xl font-bold transition-all flex items-center gap-2 shadow-xl ${
                        actionButton.disabled 
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : `${actionButton.color || 'bg-white text-slate-800'} hover:opacity-90`
                      }`}
                    >
                      <actionButton.icon className="w-5 h-5" />
                      {actionButton.text}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Enhanced Phase Progress with Database-driven states */}
              <div className="grid grid-cols-5 gap-4">
                {ASSESSMENT_PHASES.map((phase, idx) => {
                  const Icon = phase.icon;
                  
                  // Determine phase status from detailedStatus
                  let phaseStatus = 'locked';
                  let progressText = '';
                  let isComplete = false;
                  let isCurrent = false;
                  
                  if (detailedStatus?.phases) {
                    if (phase.id === 1) { // Qualitative
                      const qual = detailedStatus.phases.qualitative;
                      isComplete = qual?.status === 'completed';
                      isCurrent = qual?.status === 'in_progress';
                      phaseStatus = qual?.status || 'not_started';
                      if (isCurrent || isComplete) progressText = `${qual?.completed}/${qual?.total}`;
                    } else if (phase.id === 2) { // Payment
                      const pay = detailedStatus.phases.payment;
                      isComplete = pay?.status === 'completed';
                      isCurrent = pay?.status === 'required';
                      phaseStatus = pay?.status || 'locked';
                      if (isComplete) progressText = '✓ Paid';
                      else if (isCurrent) progressText = `$${pay?.amount?.toLocaleString()}`;
                    } else if (phase.id === 3) { // Quantitative
                      const quant = detailedStatus.phases.quantitative;
                      isComplete = quant?.status === 'completed';
                      isCurrent = quant?.status === 'in_progress' || quant?.status === 'not_started' && !quant?.locked;
                      phaseStatus = quant?.status || 'locked';
                      if (isCurrent || isComplete) progressText = `${quant?.completed}/${quant?.total}`;
                    } else if (phase.id === 4) { // Evaluation
                      const ev = detailedStatus.phases.evaluation;
                      isComplete = ev?.status === 'completed';
                      isCurrent = ev?.status === 'in_progress';
                      phaseStatus = ev?.status || 'locked';
                      if (isCurrent) progressText = `${ev?.progress}%`;
                      else if (isComplete) progressText = '✓ Done';
                    } else if (phase.id === 5) { // Results
                      const res = detailedStatus.phases.results;
                      isComplete = res?.status === 'available';
                      phaseStatus = res?.status || 'locked';
                      if (isComplete) progressText = res?.certification_tier || 'View';
                    }
                  }
                  
                  return (
                    <div 
                      key={phase.id} 
                      className={`backdrop-blur-sm rounded-xl p-4 border transition-all ${
                        isComplete 
                          ? 'bg-emerald-500/20 border-emerald-400/50' 
                          : isCurrent 
                          ? 'bg-amber-500/20 border-amber-400/50 ring-2 ring-amber-400/50' 
                          : 'bg-white/10 border-white/20'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-emerald-300" />
                        ) : isCurrent ? (
                          <Clock className="w-5 h-5 text-amber-300 animate-pulse" />
                        ) : phaseStatus === 'locked' ? (
                          <Lock className="w-5 h-5 text-white/40" />
                        ) : (
                          <Icon className="w-5 h-5 text-white/60" />
                        )}
                        <span className={`text-xs font-medium ${isComplete ? 'text-emerald-200' : isCurrent ? 'text-amber-200' : 'text-white/60'}`}>
                          Phase {phase.id}
                        </span>
                      </div>
                      <p className={`font-bold ${isComplete ? 'text-emerald-100' : isCurrent ? 'text-amber-100' : 'text-white'}`}>
                        {phase.shortName}
                      </p>
                      <p className={`text-xs mt-1 ${isComplete ? 'text-emerald-200' : isCurrent ? 'text-amber-200' : 'text-white/60'}`}>
                        {progressText || (
                          <>
                            {phase.id === 1 && `${qualitativeQuestions} Questions`}
                            {phase.id === 2 && `$${fee.toLocaleString()}`}
                            {phase.id === 3 && `${quantitativeQuestions} Questions`}
                            {phase.id === 4 && '~2 Weeks'}
                            {phase.id === 5 && 'Certification'}
                          </>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Evaluation Progress Indicator (when in evaluation) */}
          {detailedStatus?.status === 'pending_evaluation' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center relative">
                    <Clock className="w-5 h-5 text-orange-600" />
                    {/* Live indicator pulse */}
                    {wsConnected && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-900">Evaluation In Progress</h3>
                      {wsConnected && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          Live Updates
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">Our expert panel is reviewing your assessment</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">{detailedStatus.phases?.evaluation?.progress || 0}%</p>
                  <p className="text-xs text-slate-500">Estimated: ~2 weeks</p>
                </div>
              </div>
              {/* Progress Bar with animation */}
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${detailedStatus.phases?.evaluation?.progress || 0}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                {wsConnected 
                  ? 'Live updates enabled - progress bar updates in real-time as evaluators complete scoring'
                  : 'You will receive an email notification when evaluation is complete'
                }
              </p>
            </div>
          )}

          {/* View Completed Qualitative (when locked) */}
          {detailedStatus?.phases?.qualitative?.locked && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  <div>
                    <p className="font-bold text-emerald-800">Qualitative Assessment Completed</p>
                    <p className="text-sm text-emerald-600">
                      {detailedStatus.phases?.qualitative?.completed} questions answered
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => viewCompletedPhase('qualitative')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-all flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View Responses
                </button>
              </div>
            </div>
          )}

          {/* View Completed Quantitative (when completed) */}
          {detailedStatus?.phases?.quantitative?.status === 'completed' && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="font-bold text-purple-800">Quantitative Assessment Completed</p>
                    <p className="text-sm text-purple-600">
                      {detailedStatus.phases?.quantitative?.completed} questions answered
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => viewCompletedPhase('quantitative')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-all flex items-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  View Responses
                </button>
              </div>
            </div>
          )}

          {/* Analytics Preview - Always visible after qualitative completion */}
          {(detailedStatus?.phases?.qualitative?.status === 'completed' || analytics) && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900">Your Progress Analytics</h3>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center gap-1"
                >
                  View Full Analytics
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              {analytics ? (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-indigo-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-indigo-600">{analytics.overall?.total_responses || 0}</p>
                    <p className="text-xs text-slate-600">Questions Answered</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{analytics.overall?.average_score?.toFixed(1) || '0.0'}</p>
                    <p className="text-xs text-slate-600">Average Score</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-purple-600">{analytics.overall?.percentage || 0}%</p>
                    <p className="text-xs text-slate-600">Overall Progress</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">
                      {analytics.pillar_analytics?.filter(p => p.combined_average >= 3).length || 0}/8
                    </p>
                    <p className="text-xs text-slate-600">Pillars Above Average</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Complete qualitative assessment to see analytics
                </div>
              )}
            </div>
          )}

          {/* 6 Pillars Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">The 6 Pillars of Conscious Business</h3>
            <p className="text-slate-600 mb-6">
              Your assessment will evaluate your organization across all 6 pillars of the CNESS framework.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {pillarNames.length > 0 ? (
                pillarNames.map((pillar, idx) => {
                  const icons = ['👑', '❤️', '🏆', '🤝', '🏘️', '⭐'];
                  return (
                    <div key={pillar.number} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
                      <span className="text-2xl mb-2 block">{icons[idx] || '📋'}</span>
                      <p className="text-xs font-medium text-slate-700 leading-tight">{pillar.name}</p>
                    </div>
                  );
                })
              ) : (
                // Fallback while loading
                Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200 animate-pulse">
                    <span className="text-2xl mb-2 block">📋</span>
                    <div className="h-4 bg-slate-200 rounded"></div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* What to Expect */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">What to Expect</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Phase 1: Qualitative Assessment</p>
                  <p className="text-sm text-slate-600">
                    Answer {qualitativeQuestions} self-assessment questions about your organization&apos;s practices, 
                    culture, and values across all 6 pillars.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Phase 2: Payment</p>
                  <p className="text-sm text-slate-600">
                    Submit your one-time certification fee of ${fee.toLocaleString()} to proceed 
                    to the quantitative assessment phase.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Phase 3: Quantitative Assessment</p>
                  <p className="text-sm text-slate-600">
                    Provide data metrics and evidence to support your qualitative responses 
                    through {quantitativeQuestions} data-driven questions.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Phase 4: CIS Evaluation</p>
                  <p className="text-sm text-slate-600">
                    Our expert panel reviews your submission and evaluates your organization. 
                    This typically takes approximately 2 weeks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics & Reports Tab - Always visible after qualitative completion */}
      {activeTab === 'analytics' && (detailedStatus?.phases?.qualitative?.status === 'completed' || analytics) && (
        <div className="space-y-6">
          {/* Evaluation In Progress Banner - Shows when awaiting results */}
          {(detailedStatus?.status === 'pending_evaluation' || detailedStatus?.status === 'quantitative_complete') && (
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Clock className="w-7 h-7" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold mb-1">Results Are Being Compiled</h2>
                  <p className="text-amber-100">Our expert CIS panel is reviewing your complete assessment submission</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold">{detailedStatus?.phases?.evaluation?.progress || 0}%</p>
                  <p className="text-amber-100 text-sm">Evaluation Progress</p>
                </div>
              </div>
              {/* Progress Bar */}
              <div className="mt-4 w-full bg-white/20 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-white h-full rounded-full transition-all duration-500"
                  style={{ width: `${detailedStatus?.phases?.evaluation?.progress || 0}%` }}
                />
              </div>
              <p className="text-sm text-amber-100 mt-3 text-center">
                Estimated completion: ~2 weeks • You&apos;ll receive an email when results are ready
              </p>
            </div>
          )}
          
          {/* Analytics Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Assessment Analytics</h2>
                  <p className="text-indigo-200">
                    {detailedStatus?.status === 'evaluation_complete' 
                      ? 'Your final certified scores' 
                      : 'Your preliminary performance across all pillars'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{analytics?.overall?.percentage || 0}%</p>
                <p className="text-indigo-200 text-sm">
                  {detailedStatus?.status === 'evaluation_complete' ? 'Final Score' : 'Current Score'}
                </p>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <p className="text-sm text-slate-500 mb-1">Total Questions</p>
              <p className="text-3xl font-bold text-slate-900">{analytics?.overall?.total_responses || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Answered</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <p className="text-sm text-slate-500 mb-1">Average Score</p>
              <p className="text-3xl font-bold text-indigo-600">{analytics?.overall?.average_score?.toFixed(1) || '0.0'}</p>
              <p className="text-xs text-slate-400 mt-1">Out of 5.0</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <p className="text-sm text-slate-500 mb-1">Qualitative</p>
              <p className="text-3xl font-bold text-emerald-600">{analytics?.qualitative_summary?.completed || 0}/{analytics?.qualitative_summary?.total || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Completed</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <p className="text-sm text-slate-500 mb-1">Quantitative</p>
              <p className="text-3xl font-bold text-purple-600">{analytics?.quantitative_summary?.completed || 0}/{analytics?.quantitative_summary?.total || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Completed</p>
            </div>
          </div>

          {/* Pillar Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Pillar Performance</h3>
            <div className="space-y-4">
              {(analytics?.pillar_analytics || []).map((pillar, idx) => {
                const pillarName = pillarNames.find(p => p.number === pillar.pillar_number)?.name || `Pillar ${pillar.pillar_number}`;
                const percentage = (pillar.combined_average / 5) * 100;
                const colors = ['indigo', 'rose', 'emerald', 'amber', 'purple', 'teal', 'orange', 'cyan'];
                const color = colors[idx % colors.length];
                
                return (
                  <div key={pillar.pillar_number}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{['👑', '❤️', '🏆', '🤝', '🏘️', '⭐', '🌍', '⚡'][idx]}</span>
                        <span className="font-medium text-slate-700">{pillarName}</span>
                      </div>
                      <span className="font-bold text-slate-900">{pillar.combined_average.toFixed(1)}/5</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                      <div 
                        className={`bg-${color}-500 h-full rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%`, backgroundColor: `var(--${color}-500, #6366f1)` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Score Distribution */}
          {analytics?.score_distribution && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Score Distribution</h3>
              <div className="grid grid-cols-5 gap-4">
                {[
                  { level: 1, label: 'C - Starting', color: 'slate' },
                  { level: 2, label: 'N - Building', color: 'blue' },
                  { level: 3, label: 'E - Growing', color: 'emerald' },
                  { level: 4, label: 'S - Leading', color: 'purple' },
                  { level: 5, label: 'S - Standard', color: 'amber' },
                ].map((item) => (
                  <div key={item.level} className={`bg-${item.color}-50 rounded-xl p-4 text-center border border-${item.color}-200`}>
                    <p className={`text-2xl font-bold text-${item.color}-600`}>
                      {analytics.score_distribution[item.level] || 0}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evaluation Status */}
          {detailedStatus?.status === 'pending_evaluation' && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-orange-900">Evaluation In Progress</h3>
                  <p className="text-orange-700">Our expert panel is reviewing your submission</p>
                </div>
              </div>
              <div className="w-full bg-orange-200 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-orange-500 to-amber-500 h-full rounded-full transition-all"
                  style={{ width: `${analytics?.evaluation_progress || 0}%` }}
                />
              </div>
              <p className="text-sm text-orange-600 mt-2 text-center">
                Progress: {analytics?.evaluation_progress || 0}% — Estimated completion: ~2 weeks
              </p>
            </div>
          )}
        </div>
      )}

      {/* Locked Analytics Tab - When nothing is completed yet */}
      {activeTab === 'analytics' && !detailedStatus?.phases?.qualitative?.status && detailedStatus?.phases?.qualitative?.status !== 'completed' && !analytics && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Analytics Locked</h3>
          <p className="text-slate-600 max-w-md mx-auto mb-6">
            Complete your qualitative assessment to unlock analytics. Your performance data will appear here 
            as soon as you complete the first phase.
          </p>
          <button
            onClick={() => {
              setActiveTab('assessment');
              setTimeout(() => startAssessment(), 100);
            }}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all"
          >
            Start Assessment
          </button>
        </div>
      )}
        </>
      )}
    </div>
  );
};

// Embedded Assessment View Component (keeps sidebar visible)
const EmbeddedAssessmentView = ({ 
  questions, 
  sessionId, 
  organizationId, 
  organizationName,
  companySize,
  assessmentConfig,
  onBack,
  onPhaseComplete 
}) => {
  const [currentPillar, setCurrentPillar] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [showWhyMatters, setShowWhyMatters] = useState(true);
  const [showQuickWin, setShowQuickWin] = useState(false);
  const [loadingResponses, setLoadingResponses] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [bestPractice, setBestPractice] = useState('');
  const [improvementIdea, setImprovementIdea] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('qualitative'); // 'qualitative', 'payment', 'quantitative', 'evaluation'
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  // File input ref
  const fileInputRef = React.useRef(null);

  // Ref for scrolling to top
  const questionAreaRef = React.useRef(null);

  // Filter questions by phase
  const qualitativeQuestions = questions.filter(q => q.question_type === 'qualitative');
  const quantitativeQuestions = questions.filter(q => q.question_type === 'quantitative');
  
  const qualitativeTotal = assessmentConfig?.config?.qualitative_questions || assessmentConfig?.question_distribution?.qualitative || qualitativeQuestions.length || 10;
  const fee = assessmentConfig?.fee || assessmentConfig?.config?.assessment_fee || 0;
  const quantitativeTotal = assessmentConfig?.config?.quantitative_questions || assessmentConfig?.question_distribution?.quantitative || quantitativeQuestions.length || 5;

  // Get questions for current phase
  const currentPhaseQuestions = currentPhase === 'qualitative' ? qualitativeQuestions : quantitativeQuestions;
  const pillarQuestions = currentPhaseQuestions.filter(q => q.pillar_number === currentPillar);
  const currentQuestion = pillarQuestions[currentQuestionIndex];

  // Get unique pillars that have questions for current phase
  const uniquePillars = [...new Set(currentPhaseQuestions.map(q => q.pillar_number))].sort((a, b) => a - b);

  // Scroll to top when question changes
  const scrollToTop = () => {
    if (questionAreaRef.current) {
      questionAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Also scroll the window
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle file upload
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingFile(true);
    const newAttachments = [];

    for (const file of files) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        alert(`File type ${file.type} is not supported.`);
        continue;
      }

      // Create file preview
      const fileData = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        uploadedAt: new Date().toISOString(),
        status: 'ready' // 'ready', 'uploading', 'uploaded', 'error'
      };

      newAttachments.push(fileData);
    }

    setAttachments(prev => [...prev, ...newAttachments]);
    setUploadingFile(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const autoSaveInterval = setInterval(async () => {
      if (currentQuestion && (selectedLevel || additionalNotes || bestPractice || improvementIdea)) {
        setAutoSaveStatus('Saving...');
        try {
          const responseData = {
            assessment_id: sessionId,
            organization_id: organizationId,
            question_id: currentQuestion.question_id,
            phase: currentPhase,
            pillar_number: currentQuestion.pillar_number,
            selected_level: selectedLevel || 0,
            response_text: '',
            best_practice: bestPractice,
            improvement_idea: improvementIdea,
            additional_notes: additionalNotes,
            attachments: attachments.map(a => ({ name: a.name, size: a.size, type: a.type })),
            status: 'draft',
            submitted_by_user_id: organizationId
          };

          const response = await fetch(`${BACKEND_URL}/api/org-responses`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(responseData)
          });

          if (response.ok) {
            setLastAutoSave(new Date());
            setAutoSaveStatus('Draft saved');
            setTimeout(() => setAutoSaveStatus(''), 3000);
          }
        } catch (error) {
          setAutoSaveStatus('Auto-save failed');
          setTimeout(() => setAutoSaveStatus(''), 3000);
        }
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [currentQuestion?.question_id, selectedLevel, additionalNotes, bestPractice, improvementIdea, sessionId, organizationId, currentPhase]);

  // Sync local input state when question changes (NOT when responses change)
  useEffect(() => {
    if (currentQuestion) {
      const savedResponse = responses[currentQuestion.question_id];
      // Only set if we have a saved value, otherwise keep current selection
      if (savedResponse?.selected_level !== undefined) {
        setSelectedLevel(savedResponse.selected_level);
      } else {
        setSelectedLevel(null);
      }
      setAdditionalNotes(savedResponse?.additional_notes || '');
      setBestPractice(savedResponse?.best_practice || '');
      setImprovementIdea(savedResponse?.improvement_idea || '');
      // Clear attachments for new question (they need to be re-uploaded per question)
      setAttachments(savedResponse?.attachments || []);
    }
  }, [currentQuestion?.question_id]); // Remove responses from dependency array!

  // Load existing responses and determine current phase on mount
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!organizationId || !sessionId) {
        setLoadingResponses(false);
        return;
      }
      
      try {
        // Load qualitative responses
        const qualResponse = await fetch(`${BACKEND_URL}/api/org-responses/${sessionId}/${organizationId}?phase=qualitative`);
        let qualData = [];
        if (qualResponse.ok) {
          qualData = await qualResponse.json();
        }

        // Load quantitative responses
        const quantResponse = await fetch(`${BACKEND_URL}/api/org-responses/${sessionId}/${organizationId}?phase=quantitative`);
        let quantData = [];
        if (quantResponse.ok) {
          quantData = await quantResponse.json();
        }

        const responsesMap = {};
        let lastPillar = 1;
        let lastQuestionIdx = 0;
        
        // Process qualitative responses
        const submittedQualitative = qualData.filter(r => r.status === 'submitted').length;
        
        qualData.forEach(r => {
          responsesMap[r.question_id] = {
            selected_level: r.selected_level,
            response_text: r.response_text,
            best_practice: r.best_practice,
            improvement_idea: r.improvement_idea,
            additional_notes: r.additional_notes,
            attachments: r.attachments || [],
            status: r.status,
            response_id: r.response_id
          };
        });

        // Process quantitative responses
        const submittedQuantitative = quantData.filter(r => r.status === 'submitted').length;
        
        quantData.forEach(r => {
          responsesMap[r.question_id] = {
            selected_level: r.selected_level,
            response_text: r.response_text,
            best_practice: r.best_practice,
            improvement_idea: r.improvement_idea,
            additional_notes: r.additional_notes,
            attachments: r.attachments || [],
            status: r.status,
            response_id: r.response_id
          };
        });
        
        setResponses(responsesMap);

        // Determine which phase we should be in
        if (submittedQualitative >= qualitativeTotal) {
          // Check if payment is done - FIRST check database via API, then session storage
          let paymentDone = sessionStorage.getItem(`payment_done_${sessionId}`);
          
          // Always check database for payment status (in case user logged out/came back)
          try {
            const statusResponse = await fetch(`${BACKEND_URL}/api/assessment/detailed-status/${organizationId}`);
            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              // Check if payment is completed in database
              if (statusData.phases?.payment?.status === 'completed') {
                paymentDone = true;
                // Sync session storage with database state
                sessionStorage.setItem(`payment_done_${sessionId}`, 'true');
              }
            }
          } catch (e) {
            console.log('Could not fetch payment status from API');
          }
          
          if (paymentDone) {
            if (submittedQuantitative >= quantitativeTotal) {
              setCurrentPhase('evaluation');
            } else {
              setCurrentPhase('quantitative');
              // Find first unanswered quantitative question
              for (const pillar of [...new Set(quantitativeQuestions.map(q => q.pillar_number))].sort((a, b) => a - b)) {
                const pillarQs = quantitativeQuestions.filter(q => q.pillar_number === pillar);
                for (let i = 0; i < pillarQs.length; i++) {
                  const q = pillarQs[i];
                  if (!responsesMap[q.question_id] || responsesMap[q.question_id].status !== 'submitted') {
                    setCurrentPillar(pillar);
                    setCurrentQuestionIndex(i);
                    setLoadingResponses(false);
                    return;
                  }
                }
              }
            }
          } else {
            setCurrentPhase('payment');
          }
          setLoadingResponses(false);
          return;
        }
          
        // Resume qualitative from where user left off (next unanswered question)
        if (Object.keys(responsesMap).length > 0) {
          // Find first unanswered question
          for (const pillar of uniquePillars) {
            const pillarQs = qualitativeQuestions.filter(q => q.pillar_number === pillar);
            for (let i = 0; i < pillarQs.length; i++) {
              const q = pillarQs[i];
              if (!responsesMap[q.question_id] || responsesMap[q.question_id].status !== 'submitted') {
                setCurrentPillar(pillar);
                setCurrentQuestionIndex(i);
                setLoadingResponses(false);
                return;
              }
            }
          }
          // If all answered, go to payment
          setCurrentPhase('payment');
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
      } finally {
        setLoadingResponses(false);
      }
    };
    
    loadExistingResponses();
  }, [organizationId, sessionId, qualitativeQuestions.length, quantitativeQuestions.length]);

  const getCompletionStats = () => {
    const qualSubmitted = Object.entries(responses).filter(([qId, r]) => 
      r.status === 'submitted' && qualitativeQuestions.some(q => q.question_id === qId)
    ).length;
    const quantSubmitted = Object.entries(responses).filter(([qId, r]) => 
      r.status === 'submitted' && quantitativeQuestions.some(q => q.question_id === qId)
    ).length;
    
    const totalForPhase = currentPhase === 'qualitative' ? qualitativeTotal : quantitativeTotal;
    const submittedForPhase = currentPhase === 'qualitative' ? qualSubmitted : quantSubmitted;
    
    return {
      submitted: submittedForPhase,
      total: totalForPhase,
      percentage: Math.round((submittedForPhase / totalForPhase) * 100) || 0,
      qualSubmitted,
      quantSubmitted
    };
  };

  const stats = getCompletionStats();

  const handleResponseChange = (field, value) => {
    if (!currentQuestion) return;
    setResponses(prev => ({
      ...prev,
      [currentQuestion.question_id]: {
        ...prev[currentQuestion.question_id],
        [field]: value
      }
    }));
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    // Also update the responses state
    if (currentQuestion) {
      setResponses(prev => ({
        ...prev,
        [currentQuestion.question_id]: {
          ...prev[currentQuestion.question_id],
          selected_level: level
        }
      }));
    }
  };

  const handleSaveResponse = async (status = 'draft') => {
    if (!currentQuestion) return;
    if (!selectedLevel && status === 'submitted') {
      alert('Please select a level before submitting.');
      return;
    }
    
    setSaving(true);
    try {
      const responseData = {
        assessment_id: sessionId,
        organization_id: organizationId,
        question_id: currentQuestion.question_id,
        phase: currentPhase,
        pillar_number: currentQuestion.pillar_number,
        selected_level: selectedLevel || 0,
        response_text: '',
        best_practice: bestPractice,
        improvement_idea: improvementIdea,
        additional_notes: additionalNotes,
        attachments: attachments.map(a => ({ name: a.name, size: a.size, type: a.type, uploadedAt: a.uploadedAt })),
        status: status,
        submitted_by_user_id: organizationId
      };

      const response = await fetch(`${BACKEND_URL}/api/org-responses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        setResponses(prev => ({
          ...prev,
          [currentQuestion.question_id]: {
            selected_level: selectedLevel,
            additional_notes: additionalNotes,
            best_practice: bestPractice,
            improvement_idea: improvementIdea,
            attachments: attachments.map(a => ({ name: a.name, size: a.size, type: a.type })),
            status: status,
            response_id: result.response_id
          }
        }));
        
        if (status === 'submitted') {
          // Clear local input state for next question
          setSelectedLevel(null);
          setAdditionalNotes('');
          setBestPractice('');
          setImprovementIdea('');
          setAttachments([]); // Clear attachments for next question
          
          // Calculate new completion stats
          const newStats = getCompletionStats();
          const newSubmitted = newStats.submitted + 1;
          const totalForPhase = currentPhase === 'qualitative' ? qualitativeTotal : quantitativeTotal;
          
          // Check if phase is complete
          if (newSubmitted >= totalForPhase) {
            if (currentPhase === 'qualitative') {
              // Move to payment phase
              setTimeout(() => {
                setCurrentPhase('payment');
                scrollToTop();
              }, 300);
              return;
            } else if (currentPhase === 'quantitative') {
              // Move to evaluation phase
              setTimeout(() => {
                setCurrentPhase('evaluation');
                scrollToTop();
                // Update assessment status in backend
                fetch(`${BACKEND_URL}/api/assessment/update-status`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    session_id: sessionId,
                    organization_id: organizationId,
                    status: 'pending_evaluation'
                  })
                });
              }, 300);
              return;
            }
          }
          
          // Move to next question after a short delay
          setTimeout(() => {
            if (currentQuestionIndex < pillarQuestions.length - 1) {
              setCurrentQuestionIndex(currentQuestionIndex + 1);
            } else {
              // Find next pillar with questions
              const currentPillarIdx = uniquePillars.indexOf(currentPillar);
              if (currentPillarIdx < uniquePillars.length - 1) {
                setCurrentPillar(uniquePillars[currentPillarIdx + 1]);
                setCurrentQuestionIndex(0);
              }
            }
            // Scroll to top after changing question
            scrollToTop();
          }, 200);
        }
      } else {
        const errorData = await response.json();
        console.error('[Assessment] Error saving response:', errorData);
        alert('Failed to save response. Please try again.');
      }
    } catch (error) {
      console.error('Error saving response:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const getResponseStatus = (questionId) => {
    return responses[questionId]?.status || 'not_started';
  };

  // Get rubric description for a level
  const getRubricDescription = (level) => {
    if (!currentQuestion?.rubric) return null;
    const rubric = currentQuestion.rubric[level.toString()];
    return rubric?.description || rubric?.gamified_label || null;
  };

  // Handle payment submission (dummy)
  const handlePaymentSubmit = async () => {
    setSaving(true);
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mark payment as done
      sessionStorage.setItem(`payment_done_${sessionId}`, 'true');
      
      // Update assessment phase in backend
      await fetch(`${BACKEND_URL}/api/assessment/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          organization_id: organizationId,
          status: 'payment_complete',
          phase: 'quantitative'
        })
      });
      
      // Move to quantitative phase
      setCurrentPhase('quantitative');
      setCurrentPillar(1);
      setCurrentQuestionIndex(0);
      scrollToTop();
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment processing failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Show loading while fetching existing responses
  if (loadingResponses) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  // PAYMENT PHASE UI
  if (currentPhase === 'payment') {
    return (
      <div className="max-w-3xl mx-auto" ref={questionAreaRef}>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-10 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <DollarSign className="w-10 h-10" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Phase 1 Complete!</h1>
            <p className="text-emerald-100 text-lg">Congratulations on completing your Qualitative Assessment</p>
          </div>
          
          {/* Progress Summary */}
          <div className="px-8 py-6 border-b border-slate-200 bg-emerald-50">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-4xl font-bold text-emerald-600">{qualitativeTotal}</p>
                <p className="text-sm text-slate-600">Questions Completed</p>
              </div>
              <div className="w-px h-12 bg-emerald-200"></div>
              <div className="text-center">
                <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-1" />
                <p className="text-sm text-slate-600">Phase 1 Done</p>
              </div>
            </div>
          </div>
          
          {/* Payment Section */}
          <div className="px-8 py-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">Certification Assessment Fee</h2>
            <p className="text-slate-600 text-center mb-8">
              To proceed with the Quantitative Assessment phase and complete your CNESS certification, 
              please submit the one-time assessment fee.
            </p>
            
            {/* Fee Display */}
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Assessment Fee ({companySize} Enterprise)</span>
                <span className="text-3xl font-bold text-slate-900">${fee.toLocaleString()}</span>
              </div>
              <div className="space-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Includes Quantitative Assessment ({quantitativeTotal} questions)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Expert CIS Panel Evaluation (~2 weeks)</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Official CNESS Certification</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Recognition Directory Listing</span>
                </div>
              </div>
            </div>
            
            {/* Payment Button */}
            <button
              onClick={handlePaymentSubmit}
              disabled={saving}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Processing Payment...
                </>
              ) : (
                <>
                  <DollarSign className="w-5 h-5" />
                  Submit Payment & Continue to Phase 2
                </>
              )}
            </button>
            
            <p className="text-xs text-slate-400 text-center mt-4">
              This is a demo payment. Click to proceed to the Quantitative Assessment phase.
            </p>
          </div>
          
          {/* Back Button */}
          <div className="px-8 pb-8">
            <button
              onClick={onBack}
              className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // EVALUATION PHASE UI (Assessment Complete - Awaiting Results)
  if (currentPhase === 'evaluation') {
    return (
      <div className="max-w-3xl mx-auto" ref={questionAreaRef}>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-8 py-10 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="w-10 h-10" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Assessment Complete!</h1>
            <p className="text-amber-100 text-lg">Your submission is now under expert review</p>
          </div>
          
          {/* Status */}
          <div className="px-8 py-8 text-center border-b border-slate-200">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-amber-50 rounded-full border border-amber-200 mb-6">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Pending Evaluation</span>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-900 mb-4">What Happens Next?</h2>
            <p className="text-slate-600 max-w-lg mx-auto">
              Our expert CIS (Conscious Impact Society) panel will carefully review your assessment 
              responses and supporting evidence. This evaluation process typically takes approximately 
              <span className="font-bold text-amber-600"> 2 weeks</span>.
            </p>
          </div>
          
          {/* Progress Summary */}
          <div className="px-8 py-6 bg-slate-50">
            <h3 className="font-bold text-slate-900 mb-4 text-center">Your Assessment Journey</h3>
            <div className="grid grid-cols-5 gap-4">
              {[
                { name: 'Qualitative', icon: FileText, status: 'complete', color: 'emerald' },
                { name: 'Payment', icon: DollarSign, status: 'complete', color: 'emerald' },
                { name: 'Quantitative', icon: BarChart3, status: 'complete', color: 'emerald' },
                { name: 'Evaluation', icon: Clock, status: 'current', color: 'amber' },
                { name: 'Results', icon: Award, status: 'pending', color: 'slate' },
              ].map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${
                      phase.status === 'complete' ? 'bg-emerald-100' :
                      phase.status === 'current' ? 'bg-amber-100 ring-2 ring-amber-400' :
                      'bg-slate-100'
                    }`}>
                      {phase.status === 'complete' ? (
                        <CheckCircle className="w-6 h-6 text-emerald-600" />
                      ) : (
                        <Icon className={`w-6 h-6 ${
                          phase.status === 'current' ? 'text-amber-600' : 'text-slate-400'
                        }`} />
                      )}
                    </div>
                    <p className={`text-xs font-medium ${
                      phase.status === 'complete' ? 'text-emerald-600' :
                      phase.status === 'current' ? 'text-amber-600' :
                      'text-slate-400'
                    }`}>{phase.name}</p>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Completion Stats */}
          <div className="px-8 py-6 grid grid-cols-2 gap-6">
            <div className="bg-indigo-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-indigo-600">{qualitativeTotal}</p>
              <p className="text-sm text-slate-600">Qualitative Questions</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{quantitativeTotal}</p>
              <p className="text-sm text-slate-600">Quantitative Questions</p>
            </div>
          </div>
          
          {/* Info Box */}
          <div className="px-8 pb-8">
            <div className="bg-slate-800 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-bold mb-2">You&apos;ll receive an email notification</h4>
                  <p className="text-slate-300 text-sm">
                    Once the evaluation is complete, we&apos;ll notify you via email with your certification 
                    tier and detailed feedback. You can also check back here for updates.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Back Button */}
          <div className="px-8 pb-8">
            <button
              onClick={onBack}
              className="w-full py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // QUESTIONS UI (Qualitative or Quantitative phase)
  const totalForCurrentPhase = currentPhase === 'qualitative' ? qualitativeTotal : quantitativeTotal;

  return (
    <div className="space-y-4 relative" ref={questionAreaRef}>
      {/* Auto-save Status */}
      {autoSaveStatus && (
        <div className="fixed top-20 right-6 z-40 bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2">
          {autoSaveStatus === 'Saving...' ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          ) : (
            <CheckCircle className="w-4 h-4 text-emerald-400" />
          )}
          {autoSaveStatus}
        </div>
      )}

      {/* Floating Question Indicator - Always visible when scrolling - Dynamic for phase */}
      <div className={`fixed bottom-6 right-6 z-30 ${
        currentPhase === 'quantitative' 
          ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600' 
          : 'bg-gradient-to-r from-indigo-600 to-purple-600'
      } text-white px-4 py-3 rounded-xl shadow-2xl border-2 border-white/20`}>
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className={`text-xs ${currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'} uppercase`}>Question</p>
            <p className="text-lg font-bold">{currentQuestionIndex + 1}/{pillarQuestions.length}</p>
          </div>
          <div className="w-px h-8 bg-white/30"></div>
          <div className="text-center">
            <p className={`text-xs ${currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'} uppercase`}>Completed</p>
            <p className="text-lg font-bold">{stats.submitted}/{totalForCurrentPhase}</p>
          </div>
        </div>
      </div>

      {/* Header with Back Button and Progress */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg transition-all text-slate-600 border border-slate-200">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">CNESS Assessment</h1>
              <p className="text-xs text-slate-500">{organizationName} • {companySize}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            {/* Phase indicator */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg">
              <span className={`text-xs font-medium ${currentPhase === 'qualitative' ? 'text-indigo-600' : 'text-purple-600'}`}>
                {currentPhase === 'qualitative' ? 'Phase 1: Qualitative' : 'Phase 3: Quantitative'}
              </span>
            </div>
            {/* Phase Flow Mini - Dynamic highlighting */}
            <div className="hidden md:flex items-center gap-1 bg-slate-50 rounded-lg px-3 py-1.5">
              {[
                { id: 1, name: 'Qual', phase: 'qualitative' },
                { id: 2, name: 'Pay', phase: 'payment' },
                { id: 3, name: 'Quant', phase: 'quantitative' },
                { id: 4, name: 'Eval', phase: 'evaluation' },
                { id: 5, name: 'Result', phase: 'results' },
              ].map((p, idx) => {
                const isActive = p.phase === currentPhase;
                const isPast = ['qualitative', 'payment'].includes(p.phase) && currentPhase === 'quantitative';
                return (
                  <React.Fragment key={p.id}>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      isActive 
                        ? (currentPhase === 'quantitative' ? 'bg-purple-600 text-white' : 'bg-indigo-600 text-white')
                        : isPast 
                        ? 'bg-emerald-100 text-emerald-600'
                        : 'text-slate-400'
                    }`}>
                      {p.name}
                    </span>
                    {idx < 4 && <span className="text-slate-300">→</span>}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="text-right">
              <p className="text-xs font-medium text-slate-500 uppercase">Progress</p>
              <p className={`text-lg font-bold ${currentPhase === 'quantitative' ? 'text-purple-600' : 'text-indigo-600'}`}>
                {stats.submitted}/{totalForCurrentPhase}
              </p>
            </div>
            <div className="relative w-12 h-12">
              <svg className="transform -rotate-90 w-12 h-12">
                <circle cx="24" cy="24" r="20" stroke="#E2E8F0" strokeWidth="4" fill="none"/>
                <circle cx="24" cy="24" r="20" stroke={currentPhase === 'quantitative' ? '#9333EA' : '#4F46E5'} strokeWidth="4" fill="none"
                  strokeDasharray={`${stats.percentage * 1.26} 126`} strokeLinecap="round"/>
              </svg>
              <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${currentPhase === 'quantitative' ? 'text-purple-600' : 'text-indigo-600'}`}>{stats.percentage}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Banner - STICKY - Dynamic based on phase */}
      <div className={`${
        currentPhase === 'quantitative' 
          ? 'bg-gradient-to-r from-purple-600 to-fuchsia-700' 
          : 'bg-gradient-to-r from-indigo-600 to-purple-700'
      } text-white py-3 px-5 rounded-xl sticky top-20 z-10`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {currentPhase === 'quantitative' ? <BarChart3 className="w-5 h-5" /> : <FileText className="w-5 h-5" />}
            <div>
              <p className={`${currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'} text-xs uppercase tracking-wide`}>
                {currentPhase === 'quantitative' ? 'Phase 3 of 5 • Quantitative' : 'Phase 1 of 5 • Qualitative'}
              </p>
              <p className="font-bold">{currentQuestion?.pillar_name || 'Select a Pillar'}</p>
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="hidden md:block"><span className={currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'}>Size:</span> <span className="font-bold">{companySize}</span></div>
            <div><span className={currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'}>Q:</span> <span className="font-bold">{currentQuestionIndex + 1}/{pillarQuestions.length}</span></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4">
          {/* Pillar Navigation - STICKY - Dynamic for phase */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4 sticky top-36">
              <h3 className={`text-xs font-bold uppercase mb-3 ${currentPhase === 'quantitative' ? 'text-purple-600' : 'text-slate-500'}`}>
                {currentPhase === 'quantitative' ? `Pillars (${quantitativeTotal} Questions)` : `Pillars (${qualitativeTotal} Questions)`}
              </h3>
              <div className="space-y-1">
                {uniquePillars.map(pillar => {
                  const pillarQs = currentPhaseQuestions.filter(q => q.pillar_number === pillar);
                  const pillarName = pillarQs[0]?.pillar_name || `Pillar ${pillar}`;
                  const completed = pillarQs.filter(q => getResponseStatus(q.question_id) === 'submitted').length;
                  const isActive = currentPillar === pillar;
                  const isComplete = completed === pillarQs.length && pillarQs.length > 0;
                  
                  // Dynamic colors based on phase
                  const activeColor = currentPhase === 'quantitative' ? 'bg-purple-600 text-white' : 'bg-indigo-600 text-white';
                  
                  return (
                    <button
                      key={pillar}
                      onClick={() => { setCurrentPillar(pillar); setCurrentQuestionIndex(0); }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-all ${
                        isActive 
                          ? activeColor 
                          : isComplete 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-xs leading-tight">{pillarName}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                          isActive ? 'bg-white/20 text-white' : isComplete ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {completed}/{pillarQs.length}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Question Area */}
          <div className="col-span-9">
            {currentQuestion ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Question Header - Dynamic based on phase */}
                <div className={`${
                  currentPhase === 'quantitative' 
                    ? 'bg-gradient-to-r from-purple-800 to-purple-900' 
                    : 'bg-gradient-to-r from-slate-800 to-slate-900'
                } text-white px-6 py-5`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`${currentPhase === 'quantitative' ? 'bg-purple-500' : 'bg-indigo-500'} px-3 py-1 rounded-lg text-xs font-bold uppercase`}>
                        {currentPhase === 'quantitative' ? 'Quantitative' : 'Qualitative'}
                      </span>
                      <span className="bg-white/10 px-3 py-1 rounded text-xs">
                        Question {currentQuestionIndex + 1} of {pillarQuestions.length}
                      </span>
                    </div>
                    <span className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase ${
                      getResponseStatus(currentQuestion.question_id) === 'submitted' ? 'bg-emerald-500' : 'bg-white/10'
                    }`}>
                      {getResponseStatus(currentQuestion.question_id) === 'submitted' ? '✓ Completed' : 'In Progress'}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-white">{currentQuestion.pillar_name}</p>
                  <p className={`${currentPhase === 'quantitative' ? 'text-purple-200' : 'text-indigo-200'} text-sm mt-1`}>{currentQuestion.sub_pillar}</p>
                </div>

                {/* Question Content */}
                <div className="p-6 space-y-6">
                  {/* Question Text */}
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 leading-relaxed mb-2">
                      {currentQuestion.question_text}
                    </h3>
                  </div>

                  {/* Why This Matters */}
                  {currentQuestion.why_this_matters?.enabled && currentQuestion.why_this_matters?.text && (
                    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                      <button 
                        onClick={() => setShowWhyMatters(!showWhyMatters)} 
                        className="flex items-center gap-2 text-indigo-700 font-bold text-sm w-full"
                      >
                        <Info className="w-5 h-5" />
                        <span>Why This Matters</span>
                        <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${showWhyMatters ? 'rotate-90' : ''}`} />
                      </button>
                      {showWhyMatters && (
                        <p className="mt-3 text-sm text-indigo-800 leading-relaxed">{currentQuestion.why_this_matters.text}</p>
                      )}
                    </div>
                  )}

                  {/* Quick Win */}
                  {currentQuestion.quick_win?.enabled && currentQuestion.quick_win?.text && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <button 
                        onClick={() => setShowQuickWin(!showQuickWin)} 
                        className="flex items-center gap-2 text-emerald-700 font-bold text-sm w-full"
                      >
                        <Sparkles className="w-5 h-5" />
                        <span>Quick Win Suggestion</span>
                        <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${showQuickWin ? 'rotate-90' : ''}`} />
                      </button>
                      {showQuickWin && (
                        <p className="mt-3 text-sm text-emerald-800 leading-relaxed">{currentQuestion.quick_win.text}</p>
                      )}
                    </div>
                  )}

                  {/* C-N-E-S-S Selection with Full Rubric */}
                  <div>
                    {/* Rubric Legend */}
                    <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Evaluation Dimensions (C-N-E-S-S)</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                          <span className="w-5 h-5 bg-slate-200 rounded flex items-center justify-center font-bold text-slate-600">C</span>
                          <span className="text-slate-600">Starting</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 rounded text-xs">
                          <span className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center font-bold text-blue-600">N</span>
                          <span className="text-blue-600">Foundation</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded text-xs">
                          <span className="w-5 h-5 bg-emerald-100 rounded flex items-center justify-center font-bold text-emerald-600">E</span>
                          <span className="text-emerald-600">Growing</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 rounded text-xs">
                          <span className="w-5 h-5 bg-purple-100 rounded flex items-center justify-center font-bold text-purple-600">S</span>
                          <span className="text-purple-600">Leading</span>
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 rounded text-xs">
                          <span className="w-5 h-5 bg-amber-100 rounded flex items-center justify-center font-bold text-amber-600">S</span>
                          <span className="text-amber-600">Standard</span>
                        </span>
                      </div>
                    </div>
                    
                    <label className="block text-sm font-bold text-slate-700 mb-4 uppercase tracking-wide">
                      Select Your Level (C-N-E-S-S Scoring)
                    </label>
                    <div className="space-y-3">
                      {[
                        { level: 1, letter: 'C', label: 'Starting the Journey', icon: '🌱', color: 'slate' },
                        { level: 2, letter: 'N', label: 'Building Foundation', icon: '🌿', color: 'blue' },
                        { level: 3, letter: 'E', label: 'Growing Strong', icon: '🌳', color: 'emerald' },
                        { level: 4, letter: 'S', label: 'Leading the Way', icon: '🌲', color: 'purple' },
                        { level: 5, letter: 'S', label: 'Setting the Standard', icon: '🏆', color: 'amber' },
                      ].map((lvl) => {
                        const isSelected = selectedLevel === lvl.level;
                        const rubricDesc = getRubricDescription(lvl.level);
                        
                        return (
                          <div
                            key={lvl.level}
                            data-testid={`level-select-${lvl.level}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleLevelSelect(lvl.level);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleLevelSelect(lvl.level);
                              }
                            }}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all cursor-pointer select-none ${
                              isSelected 
                                ? `border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500 ring-offset-2` 
                                : 'border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-4 pointer-events-none">
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-2xl">{lvl.icon}</span>
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-black text-xl ${
                                  lvl.color === 'slate' ? 'bg-slate-100 text-slate-600' :
                                  lvl.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                                  lvl.color === 'emerald' ? 'bg-emerald-100 text-emerald-600' :
                                  lvl.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                                  'bg-amber-100 text-amber-600'
                                }`}>
                                  {lvl.letter}
                                </div>
                              </div>
                              <div className="flex-1">
                                <p className="font-bold text-slate-900">{lvl.label}</p>
                                {rubricDesc && (
                                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{rubricDesc}</p>
                                )}
                              </div>
                              {isSelected && (
                                <CheckCircle className="w-6 h-6 text-indigo-600 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Additional Notes */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Additional Notes <span className="font-normal text-slate-400">(Optional)</span>
                    </label>
                    <textarea
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      placeholder="Add any additional context or comments about your response..."
                      rows={3}
                      data-testid="additional-notes-textarea"
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800"
                    />
                  </div>

                  {/* Evidence / Attachment - Always Enabled */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Evidence / Attachments
                      <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Optional
                      </span>
                    </label>
                    <p className="text-sm text-slate-500 mb-3">
                      Upload supporting documents, images, or videos as evidence for your response.
                    </p>
                    
                    {/* Upload Area */}
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-50'); }}
                      onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50');
                        const files = e.dataTransfer.files;
                        if (files.length > 0) {
                          handleFileSelect({ target: { files } });
                        }
                      }}
                      className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer bg-slate-50"
                      data-testid="file-upload-area"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="file-input"
                      />
                      {uploadingFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          <span className="text-indigo-600">Processing files...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-slate-600">Click to upload or drag and drop</p>
                          <p className="text-xs text-slate-400 mt-1">
                            Supports: PDF, DOC, DOCX, JPG, PNG, GIF, MP4, MOV (Max 10MB per file)
                          </p>
                        </>
                      )}
                    </div>

                    {/* Uploaded Files List */}
                    {attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase">Attached Files ({attachments.length})</p>
                        {attachments.map((file) => (
                          <div 
                            key={file.id}
                            className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                file.type.includes('pdf') ? 'bg-red-100 text-red-600' :
                                file.type.includes('image') ? 'bg-green-100 text-green-600' :
                                file.type.includes('video') ? 'bg-purple-100 text-purple-600' :
                                'bg-blue-100 text-blue-600'
                              }`}>
                                {file.type.includes('pdf') ? '📄' :
                                 file.type.includes('image') ? '🖼️' :
                                 file.type.includes('video') ? '🎥' : '📎'}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">{file.name}</p>
                                <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(file.id)}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove file"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Share Best Practice */}
                  {currentQuestion.best_practices?.enabled && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <label className="block text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                        <Award className="w-5 h-5" />
                        Share a best practice you&apos;re proud of <span className="font-normal text-amber-600">(Optional)</span>
                      </label>
                      <textarea
                        value={bestPractice}
                        onChange={(e) => setBestPractice(e.target.value)}
                        placeholder={currentQuestion.best_practices?.prompt || "Tell us about something your organization does exceptionally well in this area..."}
                        rows={3}
                        className="w-full px-4 py-3 border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-slate-800"
                      />
                    </div>
                  )}

                  {/* Continuous Improvement */}
                  {currentQuestion.continuous_improvement?.enabled && (
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                      <label className="block text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        What&apos;s one thing you&apos;d like to improve here? <span className="font-normal text-purple-600">(Optional)</span>
                      </label>
                      <textarea
                        value={improvementIdea}
                        onChange={(e) => setImprovementIdea(e.target.value)}
                        placeholder={currentQuestion.continuous_improvement?.prompt || "Share any improvement ideas or areas you want to focus on..."}
                        rows={3}
                        className="w-full px-4 py-3 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-slate-800"
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => {
                        if (currentQuestionIndex > 0) {
                          setCurrentQuestionIndex(currentQuestionIndex - 1);
                        } else {
                          const currentPillarIdx = uniquePillars.indexOf(currentPillar);
                          if (currentPillarIdx > 0) {
                            const prevPillar = uniquePillars[currentPillarIdx - 1];
                            const prevPillarQs = qualitativeQuestions.filter(q => q.pillar_number === prevPillar);
                            setCurrentPillar(prevPillar);
                            setCurrentQuestionIndex(prevPillarQs.length - 1);
                          }
                        }
                      }}
                      disabled={currentPillar === uniquePillars[0] && currentQuestionIndex === 0}
                      className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Previous
                    </button>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => handleSaveResponse('draft')}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-all disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save Draft'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSaveResponse('submitted')}
                        disabled={saving || !selectedLevel}
                        className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                      >
                        Submit & Continue
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Question Navigation Footer */}
                <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-600">
                      <span className="font-semibold">{currentQuestion.pillar_name}</span> • Question {currentQuestionIndex + 1} of {pillarQuestions.length}
                    </p>
                    <div className="flex gap-1.5">
                      {pillarQuestions.map((q, idx) => (
                        <button
                          key={q.question_id}
                          onClick={() => setCurrentQuestionIndex(idx)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                            idx === currentQuestionIndex
                              ? 'bg-indigo-600 text-white shadow-md'
                              : getResponseStatus(q.question_id) === 'submitted'
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                          }`}
                        >
                          {idx + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No questions available for this pillar</p>
                <p className="text-slate-400 text-sm mt-2">Select another pillar from the sidebar</p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

// View Responses Component - Read-only view of completed phase responses
const ViewResponsesModal = ({ 
  sessionId, 
  organizationId, 
  phase, 
  onClose 
}) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/assessment/view-responses/${sessionId}/${organizationId}/${phase}`
        );
        if (response.ok) {
          const result = await response.json();
          setData(result);
        }
      } catch (error) {
        console.error('Error fetching responses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResponses();
  }, [sessionId, organizationId, phase]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  const levelLabels = {
    1: { label: 'C - Starting the Journey', color: 'slate', icon: '🌱' },
    2: { label: 'N - Building Foundation', color: 'blue', icon: '🌿' },
    3: { label: 'E - Growing Strong', color: 'emerald', icon: '🌳' },
    4: { label: 'S - Leading the Way', color: 'purple', icon: '🌲' },
    5: { label: 'S - Setting the Standard', color: 'amber', icon: '🏆' },
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 ${phase === 'qualitative' ? 'bg-emerald-600' : 'bg-purple-600'} text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold capitalize">{phase} Assessment Responses</h2>
                <p className="text-white/80 text-sm">
                  {data?.answered_questions || 0} of {data?.total_questions || 0} questions answered
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Lock Notice */}
        {data?.is_locked && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-600" />
            <p className="text-amber-800 text-sm">
              This phase is locked. Responses cannot be modified.
            </p>
          </div>
        )}

        {/* Responses List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {(data?.questions_with_responses || []).map((item, idx) => {
            const q = item.question;
            const r = item.response;
            const levelInfo = levelLabels[r.selected_level];

            return (
              <div key={q.question_id} className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                {/* Question Header */}
                <div className="bg-white px-5 py-4 border-b border-slate-200">
                  <div className="flex items-start gap-3">
                    <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded-lg text-sm font-bold">
                      Q{idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{q.question_text}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Pillar {q.pillar_number}: {q.pillar_name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Response */}
                <div className="px-5 py-4">
                  {r.status === 'submitted' ? (
                    <div className="space-y-4">
                      {/* Selected Level */}
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-semibold mb-2">Selected Level</p>
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-${levelInfo?.color}-100 border border-${levelInfo?.color}-200`}>
                          <span className="text-xl">{levelInfo?.icon}</span>
                          <span className={`font-bold text-${levelInfo?.color}-700`}>{levelInfo?.label}</span>
                        </div>
                      </div>

                      {/* Notes */}
                      {r.additional_notes && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Additional Notes</p>
                          <p className="text-slate-700 bg-white p-3 rounded-lg border border-slate-200">{r.additional_notes}</p>
                        </div>
                      )}

                      {/* Best Practice */}
                      {r.best_practice && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Best Practice Shared</p>
                          <p className="text-slate-700 bg-white p-3 rounded-lg border border-slate-200">{r.best_practice}</p>
                        </div>
                      )}

                      {/* Improvement Idea */}
                      {r.improvement_idea && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">What&apos;s one thing you&apos;d like to improve here?</p>
                          <p className="text-slate-700 bg-white p-3 rounded-lg border border-slate-200">{r.improvement_idea}</p>
                        </div>
                      )}

                      {/* Attachments */}
                      {r.attachments && r.attachments.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Attachments ({r.attachments.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {r.attachments.map((att, attIdx) => (
                              <div key={attIdx} className="bg-white px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 flex items-center gap-2">
                                <Upload className="w-4 h-4 text-slate-400" />
                                <span>{att.name || att}</span>
                                {att.size && <span className="text-xs text-slate-400">({(att.size / 1024).toFixed(1)} KB)</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Submitted At */}
                      <p className="text-xs text-slate-400">
                        Submitted: {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-400">
                      <p>Not answered</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-slate-800 text-white rounded-xl font-semibold hover:bg-slate-900 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssessmentEngine;
